import { AdminNavSection } from './adminNavigation';

const STAFF_USER_TYPES = ['super_admin', 'client_admin', 'employee'] as const;

// ─────────────────────────────────────────────────────────────────────────────
// CITY / ZONE ADMIN (employee + city_admin role — scoped to assigned zones)
// ─────────────────────────────────────────────────────────────────────────────
export const cityAdminNavSections: AdminNavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'Dashboard' },
      { label: 'Zone Users & Staff', path: '/users', icon: 'People', requiredPermissions: ['settings.users.view'] },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    accent: '#0ea5e9',
    items: [
      { label: 'Dispatch Center', path: '/dispatcher', icon: 'LocalShipping', modules: ['transportation', 'food', 'grocery'], requiredPermissions: ['dispatcher.dashboard.view', 'transportation.rides.view'] },
      { label: 'SOS Command', path: '/dispatcher/sos', icon: 'Warning', modules: ['transportation'], requiredPermissions: ['dispatcher.sos.view'] },
      { label: 'Zones', path: '/zones', icon: 'Public', requiredPermissions: ['settings.masters.view'] },
    ],
  },
  {
    id: 'onboarding',
    label: 'Partners',
    accent: '#7c3aed',
    items: [
      { label: 'Partners', path: '/partners', icon: 'Handshake', requiredPermissions: ['partners.view'] },
      { label: 'Partner Subscriptions', path: '/partners/subscription-plans', icon: 'CardMembership', requiredPermissions: ['partners.view'] },
      { label: 'Documents', path: '/config/documents', icon: 'Description', requiredPermissions: ['documents.verify'] },
    ],
  },
  {
    id: 'finance',
    label: 'Reports',
    accent: '#f59e0b',
    items: [
      { label: 'Reports', path: '/reports', icon: 'Assessment', requiredPermissions: ['analytics.reports.view'] },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// EMPLOYEE (Staff — permission-gated subset of Client Admin)
// ─────────────────────────────────────────────────────────────────────────────
export const employeeNavSections: AdminNavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'Dashboard' },
      { label: 'Notifications', path: '/notifications', icon: 'Notifications' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    accent: '#0ea5e9',
    items: [
      { label: 'Dispatcher', path: '/dispatcher', icon: 'LocalShipping', modules: ['transportation', 'food', 'grocery'], requiredPermissions: ['transportation.rides.view', 'food.orders.view'] },
      { label: 'SOS Command', path: '/dispatcher/sos', icon: 'Warning', modules: ['transportation'], requiredPermissions: ['dispatcher.sos.view', 'transportation.rides.view'] },
      { label: 'Zones', path: '/zones', icon: 'Public', requiredPermissions: ['settings.masters.view'] },
    ],
  },
  {
    id: 'onboarding',
    label: 'Partners',
    accent: '#7c3aed',
    items: [
      { label: 'Partners', path: '/partners', icon: 'Handshake', requiredPermissions: ['partners.view'] },
      { label: 'Partner Subscriptions', path: '/partners/subscription-plans', icon: 'CardMembership', requiredPermissions: ['partners.view'] },
      { label: 'Documents', path: '/config/documents', icon: 'Description', requiredPermissions: ['documents.verify'] },
    ],
  },
  {
    id: 'service_modules',
    label: 'Service Hubs',
    accent: '#0f766e',
    items: [
      { label: 'Transport', path: '/hubs/transport', icon: 'DirectionsCar', modules: ['transportation'], requiredPermissions: ['transportation.rides.view'] },
      { label: 'Delivery', path: '/hubs/delivery', icon: 'DeliveryDining', modules: ['food', 'grocery', 'porter'], requiredPermissions: ['food.orders.view', 'grocery.orders.view', 'porter.orders.view'] },
      { label: 'On Demand', path: '/hubs/on_demand', icon: 'Build', modules: ['on_demand'] },
    ],
  },
  {
    id: 'finance',
    label: 'Finance & Reports',
    accent: '#f59e0b',
    items: [
      { label: 'Reports', path: '/reports', icon: 'Assessment', requiredPermissions: ['analytics.reports.view'] },
      { label: 'Settlements', path: '/settlements', icon: 'Payments', requiredPermissions: ['analytics.reports.view'] },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    accent: '#8b5cf6',
    items: [
      { label: 'Helpdesk', path: '/helpdesk', icon: 'SupportAgent' },
    ],
  },
];

export function isStaffUserType(userType?: string): boolean {
  return STAFF_USER_TYPES.includes(userType as (typeof STAFF_USER_TYPES)[number]);
}

// ─────────────────────────────────────────────────────────────────────────────
// DRIVER
// ─────────────────────────────────────────────────────────────────────────────
export const driverNavSections: AdminNavSection[] = [
  {
    id: 'overview',
    label: 'Driver',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'Dashboard' },
      { label: 'Notifications', path: '/notifications', icon: 'Notifications' },
    ],
  },
  {
    id: 'rides',
    label: 'My Rides',
    accent: '#2563eb',
    items: [
      { label: 'Active / Incoming', path: '/hubs/transport/transport/bookings', icon: 'DirectionsCar', modules: ['transportation'] },
      { label: 'My Rides', path: '/hubs/transport/transport/bookings', icon: 'LocalTaxi', modules: ['transportation'] },
      { label: 'Scheduled Rides', path: '/hubs/transport/transport/bookings?mode=scheduled', icon: 'EventNote', modules: ['transportation'] },
    ],
  },
  {
    id: 'earnings',
    label: 'Earnings',
    accent: '#f59e0b',
    items: [
      { label: 'Earnings Summary', path: '/hubs/transport/transport/earnings', icon: 'Payments', modules: ['transportation'] },
      { label: 'Incentives & Bonuses', path: '/hubs/transport/transport/earnings?tab=incentives', icon: 'Star', modules: ['transportation'] },
      { label: 'Settlements', path: '/settlements', icon: 'AccountBalance' },
      { label: 'Wallet', path: '/wallet', icon: 'AccountBalanceWallet', modules: ['wallet'], feature: 'wallet' },
    ],
  },
  {
    id: 'profile',
    label: 'My Profile',
    items: [
      { label: 'Profile & Documents', path: '/my-profile', icon: 'VerifiedUser' },
      { label: 'Vehicle Details', path: '/my-profile?tab=vehicle', icon: 'DirectionsCar' },
      { label: 'Bank Account', path: '/my-profile?tab=bank', icon: 'AccountBalance' },
      { label: 'Reviews & Ratings', path: '/hubs/transport/transport/reviews', icon: 'Star', modules: ['transportation'] },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// MERCHANT
// ─────────────────────────────────────────────────────────────────────────────
export const merchantNavSections: AdminNavSection[] = [
  {
    id: 'overview',
    label: 'Vendor Hub',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'Dashboard' },
      { label: 'Notifications', path: '/notifications', icon: 'Notifications' },
    ],
  },
  {
    id: 'orders',
    label: 'Orders',
    accent: '#ea580c',
    items: [
      { label: 'Live Food Orders', path: '/vendor/food/live-orders', icon: 'Restaurant', modules: ['food'] },
      { label: 'Food Order History', path: '/vendor/food/order-history', icon: 'ReceiptLong', modules: ['food'] },
      { label: 'Scheduled Food Orders', path: '/vendor/food/scheduled-orders', icon: 'EventNote', modules: ['food'] },
      { label: 'Live Grocery Orders', path: '/vendor/grocery/live-orders', icon: 'ShoppingCart', modules: ['grocery'] },
      { label: 'Grocery Order History', path: '/vendor/grocery/order-history', icon: 'History', modules: ['grocery'] },
    ],
  },
  {
    id: 'food_catalog',
    label: 'Food — Restaurant',
    accent: '#d97706',
    items: [
      { label: 'Restaurant Profile', path: '/food/restaurants', icon: 'Storefront', modules: ['food'] },
      { label: 'Menu Catalog', path: '/food/menu', icon: 'MenuBook', modules: ['food'] },
      { label: 'Add Menu Item', path: '/food/items/new', icon: 'PostAdd', modules: ['food'] },
      { label: 'Addon Groups', path: '/food/addon-groups', icon: 'Extension', modules: ['food'] },
      { label: 'Categories & Variants', path: '/hubs/delivery/food-delivery/masters', icon: 'Category', modules: ['food'] },
      { label: 'Opening Hours', path: '/food/restaurants?tab=hours', icon: 'Schedule', modules: ['food'] },
      { label: 'Offers & Coupons', path: '/vendor/promos', icon: 'LocalOffer', modules: ['food'], feature: 'promo' },
    ],
  },
  {
    id: 'grocery_catalog',
    label: 'Grocery — Store',
    accent: '#00695C',
    items: [
      { label: 'Grocery Stores', path: '/grocery/stores', icon: 'Store', modules: ['grocery'] },
      { label: 'Products / Inventory', path: '/grocery/products', icon: 'Inventory', modules: ['grocery'] },
      { label: 'Add Grocery Product', path: '/grocery/products/new', icon: 'PostAdd', modules: ['grocery'] },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    items: [
      { label: 'Settlements', path: '/settlements', icon: 'Payments' },
      { label: 'Wallet', path: '/wallet', icon: 'AccountBalanceWallet', modules: ['wallet'], feature: 'wallet' },
      { label: 'Invoices & Reports', path: '/reports', icon: 'Assessment' },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    items: [
      { label: 'Profile & Documents', path: '/my-profile', icon: 'VerifiedUser' },
    ],
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    items: [
      { label: 'Onboarding Status', path: '/onboarding/merchant_onboarding', icon: 'AssignmentInd' },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// DELIVERY PARTNER
// ─────────────────────────────────────────────────────────────────────────────
export const deliveryPartnerNavSections: AdminNavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'Dashboard' },
      { label: 'Notifications', path: '/notifications', icon: 'Notifications' },
    ],
  },
  {
    id: 'deliveries',
    label: 'My Deliveries',
    accent: '#ea580c',
    items: [
      { label: 'Active Delivery', path: '/hubs/delivery/food-delivery/bookings', icon: 'DeliveryDining', modules: ['food'] },
      { label: 'Food Deliveries', path: '/hubs/delivery/food-delivery/bookings', icon: 'Restaurant', modules: ['food'] },
      { label: 'Grocery Deliveries', path: '/hubs/delivery/grocery-delivery/bookings', icon: 'ShoppingCart', modules: ['grocery'] },
      { label: 'Porter Orders', path: '/hubs/delivery/porter-service/bookings', icon: 'LocalShipping', modules: ['porter'] },
      { label: 'Delivery History', path: '/hubs/delivery/food-delivery/order-history', icon: 'History', modules: ['food'] },
    ],
  },
  {
    id: 'earnings',
    label: 'Earnings',
    accent: '#f59e0b',
    items: [
      { label: 'Earnings Summary', path: '/hubs/delivery/food-delivery/earnings', icon: 'Payments', modules: ['food', 'grocery', 'porter'] },
      { label: 'Incentives', path: '/hubs/delivery/food-delivery/earnings?tab=incentives', icon: 'Star', modules: ['food', 'grocery', 'porter'] },
      { label: 'Settlements', path: '/settlements', icon: 'AccountBalance' },
      { label: 'Wallet', path: '/wallet', icon: 'AccountBalanceWallet', modules: ['wallet'], feature: 'wallet' },
    ],
  },
  {
    id: 'profile',
    label: 'My Profile',
    items: [
      { label: 'Profile & Documents', path: '/my-profile', icon: 'VerifiedUser' },
      { label: 'Vehicle Info', path: '/my-profile?tab=vehicle', icon: 'TwoWheeler' },
      { label: 'Bank Account', path: '/my-profile?tab=bank', icon: 'AccountBalance' },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// SERVICE PROVIDER
// ─────────────────────────────────────────────────────────────────────────────
export const serviceProviderNavSections: AdminNavSection[] = [
  {
    id: 'overview',
    label: 'Service Provider',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'Dashboard' },
      { label: 'Notifications', path: '/notifications', icon: 'Notifications' },
      { label: 'Profile & Documents', path: '/my-profile', icon: 'VerifiedUser' },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// CUSTOMER
// ─────────────────────────────────────────────────────────────────────────────
export const customerNavSections: AdminNavSection[] = [
  {
    id: 'overview',
    label: 'My Account',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'Dashboard' },
      { label: 'Notifications', path: '/notifications', icon: 'Notifications' },
    ],
  },
  {
    id: 'services',
    label: 'Services',
    accent: '#6366f1',
    items: [
      { label: 'Book a Ride', path: '/hubs/transport', icon: 'DirectionsCar', modules: ['transportation'] },
      { label: 'Order Food', path: '/hubs/delivery/food-delivery', icon: 'Restaurant', modules: ['food'] },
      { label: 'Grocery', path: '/hubs/delivery/grocery-delivery', icon: 'ShoppingBasket', modules: ['grocery'] },
      { label: 'Porter', path: '/hubs/delivery/porter-service', icon: 'LocalShipping', modules: ['porter'] },
    ],
  },
  {
    id: 'activity',
    label: 'My Activity',
    accent: '#ec4899',
    items: [
      { label: 'My Rides', path: '/hubs/transport/transport/bookings', icon: 'DirectionsCar', modules: ['transportation'] },
      { label: 'My Food Orders', path: '/hubs/delivery/food-delivery/bookings', icon: 'ReceiptLong', modules: ['food'] },
      { label: 'My Grocery Orders', path: '/hubs/delivery/grocery-delivery/bookings', icon: 'ShoppingCart', modules: ['grocery'] },
    ],
  },
  {
    id: 'finance',
    label: 'Finance & Rewards',
    items: [
      { label: 'Wallet', path: '/wallet', icon: 'AccountBalanceWallet', modules: ['wallet'], feature: 'wallet' },
      { label: 'Loyalty Points', path: '/loyalty', icon: 'Stars', modules: ['loyalty'] },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// VENDOR (Event Organizer / Marketplace Vendor)
// ─────────────────────────────────────────────────────────────────────────────
export const vendorNavSections: AdminNavSection[] = [
  {
    id: 'overview',
    label: 'Event Organizer',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'Dashboard' },
      { label: 'Notifications', path: '/notifications', icon: 'Notifications' },
    ],
  },
  {
    id: 'bookings',
    label: 'Bookings & Events',
    accent: '#7c3aed',
    items: [
      { label: 'Booking Reports', path: '/reports', icon: 'Event' },
      { label: 'Offers & Promos', path: '/vendor/promos', icon: 'LocalOffer', feature: 'promo' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    items: [
      { label: 'Settlements', path: '/settlements', icon: 'Payments' },
      { label: 'Wallet', path: '/wallet', icon: 'AccountBalanceWallet', modules: ['wallet'], feature: 'wallet' },
      { label: 'Invoices & Reports', path: '/reports', icon: 'Assessment' },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    items: [
      { label: 'Profile & Documents', path: '/my-profile', icon: 'VerifiedUser' },
    ],
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    items: [
      { label: 'Onboarding Status', path: '/onboarding/event_organizer_onboarding', icon: 'AssignmentInd' },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// ROUTE ACCESS MAP
// ─────────────────────────────────────────────────────────────────────────────
export const ROLE_ROUTE_ACCESS: Record<string, string[]> = {
  merchant: [
    '/dashboard', '/notifications', '/account', '/hubs/delivery', '/hubs/delivery/food-delivery', '/hubs/delivery/grocery-delivery',
    '/food', '/grocery', '/vendor', '/settlements', '/payment-transactions', '/wallet', '/reports', '/my-profile', '/onboarding',
    '/customers', '/trips', '/orders',
  ],
  driver: [
    '/dashboard', '/notifications', '/account', '/hubs/transport',
    '/settlements', '/wallet', '/my-profile', '/onboarding',
  ],
  delivery_partner: [
    '/dashboard', '/notifications', '/account', '/hubs/delivery',
    '/settlements', '/wallet', '/my-profile', '/onboarding',
  ],
  service_provider: [
    '/dashboard', '/notifications', '/account',
    '/settlements', '/wallet', '/my-profile', '/onboarding',
  ],
  customer: [
    '/dashboard', '/notifications', '/account', '/wallet', '/loyalty',
    '/hubs/transport', '/hubs/delivery',
  ],
  vendor: [
    '/dashboard', '/notifications', '/account', '/reports', '/settlements', '/payment-transactions', '/wallet',
    '/my-profile', '/onboarding', '/vendor', '/customers', '/trips', '/orders',
  ],
};

export function getNavSectionsForUserType(userType?: string): AdminNavSection[] {
  switch (userType) {
    case 'employee':     return employeeNavSections;
    case 'merchant':     return merchantNavSections;
    case 'driver':       return driverNavSections;
    case 'delivery_partner': return deliveryPartnerNavSections;
    case 'service_provider': return serviceProviderNavSections;
    case 'customer':     return customerNavSections;
    case 'vendor':       return vendorNavSections;
    default:             return [];
  }
}

export function getRoleHomePath(userType?: string): string {
  switch (userType) {
    case 'merchant':         return '/dashboard';
    case 'driver':           return '/hubs/transport/transport/bookings';
    case 'delivery_partner': return '/hubs/delivery/food-delivery/bookings';
    case 'service_provider': return '/dashboard';
    case 'vendor':           return '/dashboard';
    case 'customer':         return '/dashboard';
    default:                 return '/dashboard';
  }
}

export function getRoleLabel(userType?: string, roleSlugs?: string[]): string {
  if (userType === 'employee' && roleSlugs?.includes('city_admin')) return 'City Admin';
  const labels: Record<string, string> = {
    super_admin:      'Super Admin',
    client_admin:     'Client Admin',
    employee:         'Staff',
    merchant:         'Vendor (Store)',
    driver:           'Driver',
    delivery_partner: 'Delivery Partner',
    service_provider: 'Service Provider',
    customer:         'Customer',
    vendor:           'Event Organizer',
  };
  return labels[userType || ''] || (userType || 'User').replace(/_/g, ' ');
}

const CITY_ADMIN_ROUTE_PREFIXES = [
  '/dashboard', '/users', '/dispatcher', '/zones', '/partners', '/onboarding',
  '/config/documents', '/reports', '/payment-transactions', '/hubs', '/customers', '/trips', '/orders',
];

export function isPartnerPortalUserType(userType?: string): boolean {
  return Boolean(
    userType && ['merchant', 'vendor', 'driver', 'delivery_partner', 'service_provider'].includes(userType),
  );
}

export function isVendorHubUserType(userType?: string): boolean {
  return userType === 'merchant' || userType === 'vendor';
}

export function isCityAdminRole(userType?: string, roleSlugs?: string[]): boolean {
  return userType === 'employee' && Boolean(roleSlugs?.includes('city_admin'));
}

export function isPathAllowedForRole(pathname: string, userType?: string, roleSlugs?: string[]): boolean {
  if (!userType) return pathname === '/dashboard';
  if (userType === 'super_admin' || userType === 'client_admin') {
    // Partner self-profile is not an admin onboarding option.
    if (pathname.startsWith('/my-profile')) return false;
    return true;
  }
  if (isCityAdminRole(userType, roleSlugs)) {
    if (pathname === '/zones/new' || /^\/zones\/[^/]+\/edit/.test(pathname)) return false;
    return CITY_ADMIN_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  }
  if (isStaffUserType(userType)) {
    if (userType === 'employee') {
      const staffPrefixes = [
        '/dashboard', '/admin/reference', '/notifications', '/account', '/dispatcher', '/zones', '/partners', '/hubs',
        '/food', '/grocery', '/transport', '/reports', '/settlements', '/payment-transactions', '/wallet', '/loyalty',
        '/config', '/customer-app', '/audit-logs', '/users', '/onboarding', '/subscriptions',
        '/customers', '/trips', '/orders',
      ];
      return staffPrefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
    }
    return true;
  }
  const allowed = ROLE_ROUTE_ACCESS[userType];
  if (!allowed) return pathname === '/dashboard';
  return allowed.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}
